/**
 * Reconciliation — making the predicted body agree with the server's.
 *
 * On every snapshot:
 *   1. Take the authoritative state for THIS client's player: position,
 *      velocity, magazine, and — since the movement pass — every effect on
 *      the body with its numbers and every cast it is inside.
 *   2. Drop every input the server has already processed (`lastProcessedSeq`).
 *   3. REPLAY every unacked input through the sim, so the restored state lands
 *      with your last 100ms of movement already applied instead of teleporting
 *      you into the past.
 *   4. Ask: where did prediction think the body was at the acked seq? The gap
 *      is the correction the player would see. Under RECONCILE_HARD_SNAP it is
 *      bled off render-only over a few ticks (the drawn body never jumps);
 *      past it the error is a genuine teleport — a respawn, a first snapshot —
 *      and is shown as one.
 *
 * ── Always restore and replay ─────────────────────────────────────────────
 *
 * This used to snap-and-replay only when the position at the ack had
 * diverged past epsilon, on the grounds that a snap that moves you 2cm is a
 * visible twitch for no benefit. That was true while the snapshot carried
 * only position: restoring it when it already agreed changed nothing. It
 * stopped being true the moment the snapshot carried EFFECTS AND CASTS. Those
 * cannot be taken without a replay (an effect the unacked inputs applied
 * would be lost) and cannot be left untaken (a stun, a slow or a pull the
 * server put on you would go unpredicted until the position finally diverged
 * — and then by metres). Replay is deterministic: when the prediction was
 * right it lands on the same bits, and the render offset is started from the
 * exact drawn position either way, so nothing on screen moves that was not
 * going to.
 *
 * Nothing here trusts the client. Every correction moves the client TOWARD the
 * server. There is no branch where a local value wins.
 */

import { RECONCILE_EPSILON, RECONCILE_HARD_SNAP } from '../config.js';

/** Scratch for the pre-snap on-screen position. One reconcile at a time. */
const _visual = { x: 0, y: 0, z: 0 };

export class Reconciler {
  constructor(predictor, inputSender) {
    this.predictor = predictor;
    this.sender = inputSender;

    /** seq -> predicted position and magazine after that seq was applied. */
    this._history = new Map();
    this.lastAckedSeq = 0;
    /** Diagnostics — surfaced in the debug overlay, never in a decision. */
    this.stats = { corrections: 0, hardSnaps: 0, lastError: 0, replayed: 0 };
  }

  /** Called right after the predictor steps a frame. */
  record(frame) {
    const p = this.predictor.player;
    this._history.set(frame.seq, { x: p.x, y: p.y, z: p.z, ammo: p.ammo });
    // Bound the map. Anything older than the ack is unreachable by definition.
    if (this._history.size > 256) {
      const cutoff = this.lastAckedSeq - 8;
      for (const seq of this._history.keys()) {
        if (seq < cutoff) this._history.delete(seq);
      }
    }
  }

  /**
   * Folds one authoritative snapshot in. Returns true when the prediction at
   * the acked seq had diverged (a correction the stats count), false when the
   * restore-and-replay landed where prediction already was.
   *
   * `mine` is the PlayerSnapshot for this client. `snapshot.lastProcessedSeq` is
   * the server's ack, and the two must come from the same message: acking
   * against a stale seq replays inputs the server already applied, which
   * double-counts a dash.
   */
  apply(snapshot, mine) {
    const predictor = this.predictor;
    const ackSeq = snapshot.lastProcessedSeq;
    this.lastAckedSeq = ackSeq;

    this.sender.ack(ackSeq);

    const predictedAtAck = this._history.get(ackSeq);
    this._history.delete(ackSeq);

    // Nothing to compare against: either this is the first snapshot, or the ack
    // refers to a frame we already pruned. Shown as the placement it is.
    let error = Infinity;
    if (predictedAtAck) {
      const dx = predictedAtAck.x - mine.x;
      const dy = predictedAtAck.y - mine.y;
      const dz = predictedAtAck.z - mine.z;
      error = Math.sqrt(dx * dx + dy * dy + dz * dz);
    }
    this.stats.lastError = error === Infinity ? 0 : error;

    // The magazine is predicted too, so it is compared the same way: at the
    // acked seq, not against a value that already includes presses the server
    // has not seen. A mismatch is a divergence even when the body agrees.
    const ammoDrift = predictedAtAck !== undefined && predictedAtAck.ammo !== mine.ammo;
    const diverged = error > RECONCILE_EPSILON || ammoDrift;

    // Before any replay: the unacked inputs are about to be re-stepped, and
    // stepping them through an unmutated world is how a live mutator turned
    // every correction into a fresh mispredict.
    predictor.syncChaos(snapshot.chaos);

    // The server's tick governs cooldowns and every relative tick count on
    // the wire (an effect's remaining life, a cast's resolve), so it is taken
    // before the row is.
    predictor.setTick(snapshot.tick);

    // The other bodies, where the server has them, before the replay meets them.
    predictor.syncBodies(snapshot);

    // A row without `fx` comes from a server that predates the full effect
    // row (or a test rig standing in for one). Restoring from it would wipe
    // nothing the local guess holds — the predictor's own `busy`, its own
    // pending casts — and the replay would then refuse the unacked presses
    // those were blocking. So such a row keeps the old rule: restore and
    // replay only when the ack diverged.
    const carriesState = Array.isArray(mine.fx);
    if (!diverged && !carriesState) {
      predictor.applySnapshot(mine, { snapPosition: false });
      return false;
    }

    // Where the body was DRAWN this frame, taken before the replay rewrites
    // prev/curr: the gap between this and the corrected position is exactly the
    // jump the player would otherwise see in one frame.
    const vis = predictor.captureVisual(_visual);

    predictor.applySnapshot(mine, { snapPosition: true });

    const unacked = this.sender.unacked();
    this.stats.replayed = unacked.length;
    for (const frame of unacked) {
      predictor.step(frame);
      const p = predictor.player;
      this._history.set(frame.seq, { x: p.x, y: p.y, z: p.z, ammo: p.ammo });
    }

    // A replay rewrote `prev` and `curr` several times over. Collapsing them
    // means the render interpolator does not lerp across the whole correction —
    // the authoritative snap is instant, and the frames after it are smooth.
    predictor.prev.x = predictor.curr.x = predictor.player.x;
    predictor.prev.y = predictor.curr.y = predictor.player.y;
    predictor.prev.z = predictor.curr.z = predictor.player.z;
    predictor.prev.yaw = predictor.curr.yaw = predictor.player.yaw;

    if (diverged) {
      this.stats.corrections += 1;
      if (error > RECONCILE_HARD_SNAP) this.stats.hardSnaps += 1;
    }

    if (error <= RECONCILE_HARD_SNAP) {
      // The DRAWN body stays where it was this frame and bleeds onto the
      // corrected position over a few ticks. Hit tests and the sim never see
      // the offset — it exists only inside `interpolate`. Started from the
      // drawn position whether or not the ack diverged: `vis` already carries
      // any offset still bleeding, so this continues it rather than resets it.
      predictor.beginCorrectionSmoothing(
        vis.x - predictor.curr.x,
        vis.y - predictor.curr.y,
        vis.z - predictor.curr.z
      );
    } else {
      // Includes error === Infinity (first snapshot, pruned history): initial
      // placement and true teleports are shown instantly, not slid into.
      predictor.clearCorrectionSmoothing();
    }

    return diverged;
  }

  reset() {
    this._history.clear();
    this.lastAckedSeq = 0;
  }
}
